import React, { useCallback, useMemo } from 'react';
import styles from './index.module.scss';
import { Virtuoso } from 'react-virtuoso';
import { ArticleCommentItem, FeedArticle, FeedComment, useArticleComments } from '../..';
import { useSwitch } from '@/hooks';
import clsx from 'clsx';
import { isNumber } from 'lodash';
import { IconRcStreamFoldedArrow } from '@/assets/stream';
import { useTranslation } from 'react-i18next';

export const FoldedComments = ({
  articleId,
  article,
  commentOrderBy,
  handleReplyTo,
}: {
  articleId: number;
  article: FeedArticle;
  commentOrderBy: string;
  handleReplyTo: (c: FeedComment) => void;
}) => {
  const { t } = useTranslation();
  const { on, turn } = useSwitch();

  const { data, hasNextPage, isFetchingNextPage, fetchNextPage } = useArticleComments({
    id: articleId,
    orderBy: commentOrderBy,
    isLowQuality: true,
  });
  const comments = useMemo(
    () => data?.pages?.flatMap(x => x.comments)?.filter(comment => !comment._is_hidden),
    [data],
  );

  const totalCount = useMemo(() => data?.pages?.[0]?.total_count, [data?.pages]);

  const fetchMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const handleClick = useCallback(() => {
    turn(!on);
  }, [on, turn]);

  if (!data || (isNumber(totalCount) && totalCount <= 0)) {
    return null;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header} onClick={handleClick}>
        {on
          ? t('FoldedComments.openTitle', { count: totalCount })
          : t('FoldedComments.title', { count: totalCount })}
        <IconRcStreamFoldedArrow className={clsx(styles.foldedArrow, on && styles.open)} />
      </div>
      {on ? (
        <Virtuoso
          useWindowScroll
          customScrollParent={document.querySelector('#detailModal') as HTMLElement}
          data={comments}
          overscan={10}
          endReached={fetchMore}
          itemContent={(index, item) => (
            <ArticleCommentItem
              key={item.id}
              comment={item}
              articleId={articleId}
              onReplyClick={handleReplyTo}
              noBorder={!index}
              article={article}
              articleInvisible={!article.is_visible}
              isLowQuality={true}
            />
          )}
        />
      ) : null}
    </div>
  );
};
